import { zonedTimeToUtc } from 'date-fns-tz';
import { Customer } from '@modules/customer-store';
import { RouteNames } from '@nx-ng/constants';
import { DEFAULT_TIMEZONE } from '@nx-ng/constants';
import {
  BookingSummary,
  BookingSummaryItem,
  CustomerTypeIds,
  DeliveryMethod,
  IChildEducationDetails,
  IDirectDebitBankDetails,
  IDirectDebitDetails,
  IMTicketDetails,
  IStudentDetails,
  ISwiftCardInfoDetails,
  ITicketDescriptionFields,
  SwiftCardState,
} from '@nx-ng/types';
import { TicketApplicantState } from '@nx-ng/types/previous-applicant-details';
import { getBaseUrl, padNumber } from '@nx-ng/util';
import {
  Applicant,
  Basket,
  BasketCheckoutRequest,
  SwiftCardInfo,
  StudentInfo,
  OccupationInfo,
  KnownApplicantTicketFor,
  MTicket,
  KnownOccupationInfoOccupationType,
} from '../client/src';

// NB: All validation should be performed prior to this step to prevent bad values leaking into the API
export const formatBasketCheckoutRequest = ({
  directDebitForm,
  bookingSummary,
  bankDetails,
  accountHolder,
  fares,
}: {
  directDebitForm: IDirectDebitDetails | undefined;
  bookingSummary: BookingSummary;
  accountHolder: Customer | undefined;
  bankDetails: IDirectDebitBankDetails | undefined;
  fares: ITicketDescriptionFields;
}): BasketCheckoutRequest => {
  const { billingAddress, communicationPreferences } = bookingSummary;

  return {
    applicants: bookingSummary.items.map((item) =>
      formatBasketCheckoutApplicant(item, fares)
    ),
    billingCustomer: {
      firstName: billingAddress?.firstName || '',
      lastName: billingAddress?.lastName || '',
      dateOfBirth: accountHolder?.dateOfBirth?.substring(0, 10) || '',
      mobileNumber: accountHolder?.telephoneNumber || '',
      emailAddress: accountHolder?.emailAddress || '',
    },
    billingAddress: {
      address1: billingAddress?.address?.address1 || '',
      town: billingAddress?.address?.town || '',
      address2: billingAddress?.address?.address2,
      postCode: billingAddress?.address?.postCode || '',
    },
    directDebit: bankDetails?.valid
      ? {
        bankName: bankDetails?.bankName || '',
        branchName: bankDetails?.branchName || '',
        fullName: directDebitForm?.fullName || '',
        sortCode: window.btoa(directDebitForm?.sortCode || ''),
        accountNumber: window.btoa(directDebitForm?.accountNumber || ''),
        accountVerified: true,
        accountHolderMandate: directDebitForm?.accountHolderMandate || false,
        creditCheckAgreement: directDebitForm?.creditCheckAgreement || false,
      }
      : undefined,
    preferences: {
      upcomingOffers: communicationPreferences?.marketingOptIn || false,
    },
    tandc: {
      acceptTerms: communicationPreferences?.agreeToTsAndCs || false,
      guardianConsent: communicationPreferences?.guardianConsent || false, //TODO: guardian consent should allow undefined for when it is not displayed
    },

    redirectURLs: {
      errorURL: `${getBaseUrl()}/${RouteNames.PAYMENT_FAILED}`,
      successURL: `${getBaseUrl()}/${RouteNames.PAYMENT_SUCCESS}`,
    },
  };
};

export const formatBasketCheckoutApplicant = (
  item: BookingSummaryItem,
  fares: ITicketDescriptionFields
): Applicant => {
  const ticketCode =
    item.selectedDeliveryMethod === DeliveryMethod.MTicket
      ? fares[item.basketItemId].mTicketID
      : fares[item.basketItemId].swiftTicketID;

  const dob: string = item?.customerDetails?.dateOfBirth
    ? zonedTimeToUtc(item.customerDetails.dateOfBirth, DEFAULT_TIMEZONE)
      .toISOString()
      .substring(0, 10)
    : '';

  return {
    ticketFor: formatTicketIsFor(item.ticketIsForApplicant),
    basketItemTicketID: item.basketItemId.toString(),
    basketItemQuantityIndex: item.quantityIndex,
    ticketCode: ticketCode || '',
    fulfillmentMethod:
      item.selectedDeliveryMethod === DeliveryMethod.Swift
        ? 'Smartcard'
        : 'mTicket',
    travellingCustomer: {
      id: item.customerDetails?.id,
      firstName: item.customerDetails?.firstName || '',
      lastName: item.customerDetails?.lastName || '',
      dateOfBirth: dob,
    },
    student:
      item.customerType === CustomerTypeIds.Student
        ? formatApplicantStudent(item.studentDetails)
        : undefined,
    occupation:
      item.customerType === CustomerTypeIds.Child
        ? formatApplicantOccupation(item.childEducationDetails)
        : undefined,
    swiftCard:
      item.selectedDeliveryMethod === DeliveryMethod.Swift
        ? formatApplicantSwift(item.swiftCardDetails)
        : undefined,
    mTicket:
      item.selectedDeliveryMethod === DeliveryMethod.MTicket
        ? formatApplicantMticket(item.mTicketDetails)
        : undefined,
  };
};

export const formatTicketIsFor = (
  ticketIsForApplicant?: string | number | undefined
): KnownApplicantTicketFor => {
  if (
    !ticketIsForApplicant ||
    ticketIsForApplicant === TicketApplicantState.ForMyself
  ) {
    return KnownApplicantTicketFor.Self;
  } else if (ticketIsForApplicant === TicketApplicantState.SomeoneElse) {
    return KnownApplicantTicketFor.NewApplicant;
  }
  return KnownApplicantTicketFor.KnownApplicant;
};

export const formatApplicantSwift = (
  swiftCardDetails?: ISwiftCardInfoDetails
): SwiftCardInfo => {
  const { newCardDeliveryAddress } = swiftCardDetails || {};

  const activationDate = swiftCardDetails?.activationDate
    ? swiftCardDetails.activationDate
    : undefined;

  return {
    state: swiftCardDetails?.swiftCardState || SwiftCardState.New,
    cardNumber: swiftCardDetails?.cardNumber
      ? window.btoa(swiftCardDetails.cardNumber)
      : '',
    activationDate: activationDate
      ? zonedTimeToUtc(activationDate, DEFAULT_TIMEZONE)
        .toISOString()
        .substring(0, 10)
      : '',

    newCardDeliveryAddress:
      swiftCardDetails?.swiftCardState === SwiftCardState.New
        ? {
          address1: newCardDeliveryAddress?.address1 || '',
          address2: newCardDeliveryAddress?.address2,
          town: newCardDeliveryAddress?.town || '',
          postCode: newCardDeliveryAddress?.postCode || '',
        }
        : undefined,
  };
};

export const formatApplicantMticket = (
  mTicketDetails: IMTicketDetails
): MTicket => {
  const startDate = mTicketDetails.startDate
    ? mTicketDetails.startDate
    : undefined;

  return {
    mobileNumber: mTicketDetails.mobile || '',
    emailAddress: mTicketDetails.email || '',
    startDate: startDate
      ? zonedTimeToUtc(startDate, DEFAULT_TIMEZONE)
        .toISOString()
        .substring(0, 10)
      : '',
  };
};

export const formatApplicantStudent = (
  studentDetails?: IStudentDetails
): StudentInfo => {
  const courseEndDate = studentDetails?.courseEndDate
    ? zonedTimeToUtc(studentDetails.courseEndDate, DEFAULT_TIMEZONE)
    : undefined;

  return {
    studentId: studentDetails?.studentId || '',
    institution: studentDetails?.institution || '',
    course: studentDetails?.course || '',
    courseEndDate: courseEndDate
      ? `${courseEndDate.getFullYear()}-${padNumber(
        courseEndDate.getMonth() + 1,
        2
      )}`
      : '',
  };
};

export const formatApplicantOccupation = (
  childEducationDetails?: IChildEducationDetails
): OccupationInfo => {
  return {
    occupationType: childEducationDetails?.occupation || 'Trainee',
    providerName: determineProviderName(childEducationDetails) || '',
    providerAddress: {
      address1: childEducationDetails?.address?.address1 || '',
      address2: childEducationDetails?.address?.address2 || '',
      town: childEducationDetails?.address?.town || '',
      postCode: childEducationDetails?.address?.postCode || '',
    },
  };
};

export const determineProviderName = (
  details: IChildEducationDetails | undefined
): string | undefined => {
  if (details?.occupation === undefined) {
    return '';
  }

  if (details.occupation === KnownOccupationInfoOccupationType.FullTime) {
    return details.education;
  } else if (details.occupation === KnownOccupationInfoOccupationType.Trainee) {
    return details.training;
  }
  return details.employment;
};
